import { Check, Close } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { useForm } from 'react-hook-form';
import { ImageComponentInterface } from '../../../../../interfaces/pagebuilder';
import validationBuilder from '../../../../../static/validations';
import VyUpload from '../../../../common/VyUpload';
import { PageBuilderSetting, PageBuilderSettingsSection, PageBuilderSettingsTable } from '../PageBuilderSettingsTable';
import { VyAspectRatioHint } from '../../../../common/VyAspectRatioHint';

interface ImageSettingsTableInterface {
  component: ImageComponentInterface;
  onChange: (c: ImageComponentInterface) => void;
}

interface FormProps {
  imageLink: string;
}

const ImageSettingsTable = ({ component, onChange }: ImageSettingsTableInterface) => {
  const { t } = useTranslation(['pagebuilder']);
  const validations = validationBuilder(t);
  const {
    register,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm<FormProps>({ mode: 'onChange' });

  const submit = ({ imageLink }: FormProps) => {
    onChange({ ...component, imageLink, imageFile: undefined });
  };

  function handleAddFile(fileId?: string | null, fileIds?: string[]) {
    if (!fileId && !fileIds) return;

    const _images = [...(component.images || [])];
    const _empty = _images.indexOf(null);

    if (fileId) {
      if (_empty >= 0) _images.splice(_empty, 1, fileId);
      else _images.push(fileId);
    }
    if (fileIds) fileIds.map((id) => _images.push(id));

    const _component = {
      ...component,
      images: _images,
    };
    onChange(_component);
  }

  const settingSections: PageBuilderSettingsSection[] = [
    {
      settings: [
        {
          path: 'imageComponentType',
          type: 'select' as PageBuilderSetting['type'],
          label: t('pagebuilder:imageComponentType'),
          selectItems: [
            { name: t('pagebuilder:slideshow'), value: 'slideshow' },
            { name: t('pagebuilder:image'), value: 'image' },
          ],
          defaultValue: component.imageComponentType ?? 'image',
        },
        {
          content: <VyAspectRatioHint aspectRatio={'1600 x 900'}></VyAspectRatioHint>,
        },
        ...(component.imageComponentType === 'image'
          ? [
              {
                label: t('pagebuilder:image-type'),
                path: 'imageType',
                type: 'select' as PageBuilderSetting['type'],
                selectItems: [
                  { name: t('pagebuilder:file'), value: 'file' },
                  { name: t('pagebuilder:link'), value: 'link' },
                ],
                defaultValue: component.imageType,
              },

              {
                content: (
                  <VyUpload
                    withCrop
                    customAspectRatio
                    noCropForMediaCenter
                    defaultFileId={component.imageFile}
                    onChange={(fileId) => onChange({ ...component, imageFile: fileId ?? undefined, imageLink: undefined })}
                  />
                ),
                visible: component.imageType === 'file',
              },
              {
                content: (
                  <form onSubmit={handleSubmit(submit)}>
                    <div className="flex flex-col w-full space-y-2">
                      <div className="flex flex-row w-full">
                        <input required {...register('imageLink', validations('urlWithoutProtocol', false))} type="text" defaultValue={component.imageLink} />
                        <IconButton type="submit" size="large">
                          <Check style={{ color: `${'#4caf50'}` }} />
                        </IconButton>
                        <IconButton
                          onClick={() => {
                            reset();
                            onChange({ ...component, imageLink: undefined });
                          }}
                          size="large">
                          <Close style={{ color: `${'#f44336'}` }} />
                        </IconButton>
                      </div>
                      <p className="text-red-200"> {errors.imageLink?.message} </p>
                    </div>
                  </form>
                ),
                visible: component.imageType === 'link',
              },
            ]
          : [
              {
                path: 'autoplay',
                type: 'boolean' as PageBuilderSetting['type'],
                label: t('pagebuilder:autoplay'),
              },
              {
                path: 'controls',
                type: 'boolean' as PageBuilderSetting['type'],
                label: t('pagebuilder:controls'),
              },
              {
                label: t('pagebuilder:add-image'),
                content: (
                  <VyUpload
                    id={'gallery-image'}
                    showCropButton={false}
                    showPreview={false}
                    withRotation={false}
                    acceptedFileTypes=".jpeg, .jpg, .gif, .png, .webp"
                    withCrop
                    noCropForMediaCenter
                    cropAspectRatio={
                      component.customStyling?.aspectRatio?.x && component.customStyling?.aspectRatio?.y
                        ? component.customStyling?.aspectRatio?.x / component.customStyling?.aspectRatio?.y
                        : component.customStyling?.height && component.customStyling?.width
                        ? component.customStyling?.width / component.customStyling?.height
                        : 4 / 1
                    }
                    onMultiFilesUpload={(fileIds) => handleAddFile(undefined, fileIds)}
                    onChange={(fileId) => handleAddFile(fileId)}
                  />
                ),
              },
              {
                type: 'dnd' as PageBuilderSetting['type'],
                path: 'images',
                dndItems: component.images,
              },
            ]),
      ],
    },
  ];

  return <PageBuilderSettingsTable sections={settingSections} onChange={onChange} component={component} />;
};
export default ImageSettingsTable;
